import { useState, useEffect } from 'react';
import PolicyViolationsService from '../../../../../../services/PolicyViolations';
import PerformanceDisciplinaryActionService from '../../../../../../services/PerformanceDisciplinaryAction';
import { INITIAL_FORM_DATA } from './formConstants';
import { UserWithEmployee } from '../../UserSelectionModal';

interface FormData {
  name: string;
  date: string;
  position: string;
  department: string;
  supervisor: string;
  dateOfHire: string;
  policyViolationIds: string[];
  reasonForDisciplinaryAction: string;
  actionTaken: string;
  expectedImprovement: string;
  followUpMeetingDate: string;
  privateNotes: string;
}

interface FormErrors {
  name?: string;
  date?: string;
  policyViolationIds?: string;
  reasonForDisciplinaryAction?: string;
  actionTaken?: string;
  expectedImprovement?: string;
}

interface PolicyViolation {
  id: string;
  name: string;
}

interface PriorDiscussion {
  date_of_incident: string;
  type: string;
  policy_violated: string;
  action_taken?: string;
}

export const useDisciplinaryActionForm = (
  isOpen: boolean,
  token: string,
  currentUser?: UserWithEmployee,
  submittedForms?: Map<string, FormData>,
  editMode: boolean = false,
  disciplinaryActionId?: string,
) => {
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<FormErrors>({});
  const [policyViolations, setPolicyViolations] = useState<PolicyViolation[]>([]);
  const [priorDiscussions, setPriorDiscussions] = useState<PriorDiscussion[]>([]);

  const loadPolicyViolations = async () => {
    try {
      const service = PolicyViolationsService(token);
      const response = await service.list();
      setPolicyViolations(response.data || []);
    } catch (error) {
      console.error('Error loading policy violations:', error);
      setPolicyViolations([]);
    }
  };

  const loadPriorDiscussions = async (userId: string) => {
    try {
      const service = PerformanceDisciplinaryActionService(token);
      const response = await service.getPriorDiscussions(userId);
      setPriorDiscussions(response.data || []);
    } catch (error) {
      console.error('Error loading prior discussions:', error);
      setPriorDiscussions([]);
    }
  };

  const loadDisciplinaryActionData = async (id: string) => {
    try {
      const service = PerformanceDisciplinaryActionService(token);
      const response = await service.show(id);
      const disciplinaryAction = response.data;

      if (disciplinaryAction) {
        let policyViolationIds: string[] = [];

        if (disciplinaryAction.policyViolationIds && Array.isArray(disciplinaryAction.policyViolationIds)) {
          policyViolationIds = disciplinaryAction.policyViolationIds;
        } else if (disciplinaryAction.policyViolations && Array.isArray(disciplinaryAction.policyViolations)) {
          policyViolationIds = disciplinaryAction.policyViolations.map((pv: any) => pv.id || pv);
        } else if (disciplinaryAction.policyViolationId) {
          policyViolationIds = [disciplinaryAction.policyViolationId];
        } else if (disciplinaryAction.policyViolation?.id) {
          policyViolationIds = [disciplinaryAction.policyViolation.id];
        }

        setFormData({
          name: disciplinaryAction.name || '',
          date: disciplinaryAction.date ? new Date(disciplinaryAction.date).toISOString().split('T')[0] : '',
          position: disciplinaryAction.position || '',
          department: disciplinaryAction.department || '',
          supervisor: disciplinaryAction.supervisor || '',
          dateOfHire: disciplinaryAction.dateOfHire
            ? new Date(disciplinaryAction.dateOfHire).toISOString().split('T')[0]
            : '',
          policyViolationIds,
          reasonForDisciplinaryAction: disciplinaryAction.reasonForDisciplinaryAction || '',
          actionTaken: disciplinaryAction.actionTaken || '',
          expectedImprovement: disciplinaryAction.expectedImprovement || '',
          followUpMeetingDate:
            disciplinaryAction.followUpMeetingDate || disciplinaryAction.meetingDate
              ? new Date(disciplinaryAction.followUpMeetingDate || disciplinaryAction.meetingDate)
                  .toISOString()
                  .split('T')[0]
              : '',
          privateNotes: disciplinaryAction.privateNotes || '',
        });

        if (disciplinaryAction.userId) {
          loadPriorDiscussions(disciplinaryAction.userId);
        }
      }
    } catch (error) {
      console.error('Error loading disciplinary action data:', error);
    }
  };

  useEffect(() => {
    if (isOpen && token) {
      loadPolicyViolations();
    }
  }, [isOpen, token]);

  useEffect(() => {
    if (isOpen && editMode && disciplinaryActionId && token) {
      loadDisciplinaryActionData(disciplinaryActionId);
    }
  }, [isOpen, editMode, disciplinaryActionId, token]);

  useEffect(() => {
    if (isOpen && !editMode && currentUser) {
      const savedForm = submittedForms?.get(currentUser.id);
      if (savedForm) {
        setFormData(savedForm);
      } else {
        const today = new Date().toISOString().split('T')[0];
        const lastSavedForm =
          submittedForms && submittedForms.size > 0 ? Array.from(submittedForms.values()).at(-1) : undefined;
        setFormData({
          name: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim(),
          date: today,
          position: currentUser.employee?.title?.name || '',
          department: currentUser.employee?.department?.name || '',
          supervisor: currentUser.employee?.supervisor?.name || '',
          dateOfHire: currentUser.employee?.startDate || '',
          policyViolationIds: lastSavedForm?.policyViolationIds ?? [],
          reasonForDisciplinaryAction: lastSavedForm?.reasonForDisciplinaryAction ?? '',
          actionTaken: lastSavedForm?.actionTaken ?? '',
          expectedImprovement: lastSavedForm?.expectedImprovement ?? '',
          followUpMeetingDate: lastSavedForm?.followUpMeetingDate ?? '',
          privateNotes: lastSavedForm?.privateNotes ?? '',
        });
      }
      setErrors({});
      loadPriorDiscussions(currentUser.id);
    }
  }, [isOpen, editMode, currentUser]);

  useEffect(() => {
    if (!isOpen) {
      setFormData(INITIAL_FORM_DATA);
      setErrors({});
      setPriorDiscussions([]);
    }
  }, [isOpen]);

  const handleInputChange = (field: keyof FormData, value: string | string[]) => {
    setFormData((prev: FormData) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev: FormErrors) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    const today = new Date().toISOString().split('T')[0];

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.date) {
      newErrors.date = 'Date is required';
    } else if (formData.date > today) {
      newErrors.date = 'Date cannot be in the future';
    }
    if (formData.policyViolationIds.length === 0) {
      newErrors.policyViolationIds = 'At least one Policy Violation is required';
    }
    if (!formData.reasonForDisciplinaryAction.trim()) {
      newErrors.reasonForDisciplinaryAction = 'Reason for Disciplinary Action is required';
    }
    if (!formData.actionTaken.trim()) {
      newErrors.actionTaken = 'Action Taken is required';
    }
    if (!formData.expectedImprovement.trim()) {
      newErrors.expectedImprovement = 'Expected Improvement is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return {
    formData,
    errors,
    policyViolations,
    priorDiscussions,
    handleInputChange,
    validateForm,
    setFormData,
  };
};
